import type { Language } from '@bett3r-dev/teselly-domain';
import useUserPreferences, { UserPreferencesContextType } from '@contexts/useUserPreferences/useUserPreferences';
import { Badge, Menu, SegmentedControl, Text, useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { IconChevronRight, IconLanguage, IconMoon, IconProgressHelp, IconSun } from '@tabler/icons-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import { TourEntriesEmptyState } from '../TourMenu/TourEntriesEmptyState';
import { useTourEntries } from '../TourMenu/useTourEntries';
import classes from './PreferencesSection.module.css';

const ICON_COLOR = 'var(--mantine-color-primary-7)';

type PreferencesSectionProps = {
  /** Closes the parent account menu — used when picking a tutorial navigates away. */
  onCloseMenu: () => void;
};

/**
 * Preferences block rendered inside the AccountMenu dropdown, above Logout.
 * Holds the three settings that used to be standalone navbar icons (TV1-2299):
 * theme, language and tutorials. Theme and language flip in place and keep the
 * menu open; picking a tutorial starts it and closes everything.
 */
export function PreferencesSection({ onCloseMenu }: PreferencesSectionProps ) {
  const { t, i18n } = useTranslation( 'account' );
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme( 'light' );
  const { updateLanguage } = useUserPreferences() as UserPreferencesContextType;
  const isLanguageChangeEnabled = useBooleanFlagValue( 'languageChange', false );
  const { entries, startTour } = useTourEntries();

  const isDark = computedColorScheme === 'dark';
  // i18n.language can carry a region suffix (es-AR) — match on the prefix, not equality.
  const currentLanguage: Language = i18n.language?.startsWith( 'es' ) ? 'es' : 'en';

  const handleStartTour = ( tourKey: string ) => {
    startTour( tourKey );
    onCloseMenu();
  };

  return (
    <>
      <Menu.Divider />
      <Menu.Label>{t( 'preferences_label', 'Preferencias' )}</Menu.Label>

      {/* Theme — the whole item is the toggle; the track is a decorative span so it
          stays valid (and clickable) inside the item's <button>. */}
      <Menu.Item
        py="sm"
        role="switch"
        aria-checked={isDark}
        closeMenuOnClick={false}
        leftSection={
          isDark
            ? <IconSun color={ICON_COLOR} size={14} />
            : <IconMoon color={ICON_COLOR} size={14} />
        }
        rightSection={
          <span className={classes.switchTrack} data-checked={isDark || undefined}>
            <span className={classes.switchThumb} />
          </span>
        }
        onClick={() => setColorScheme( isDark ? 'light' : 'dark' )}
      >
        {t( 'preferences_dark_mode_label', 'Modo oscuro' )}
      </Menu.Item>

      {/* Language — rendered as a div so the SegmentedControl's radio inputs are not
          nested inside a <button>, which would swallow their clicks. */}
      {isLanguageChangeEnabled && (
        <Menu.Item
          component="div"
          py="sm"
          closeMenuOnClick={false}
          className={classes.staticItem}
          leftSection={<IconLanguage color={ICON_COLOR} size={14} />}
          rightSection={
            <SegmentedControl
              size="xs"
              value={currentLanguage}
              onChange={( value ) => updateLanguage( value as Language )}
              data={[
                { label: 'ES', value: 'es' },
                { label: 'EN', value: 'en' }
              ]}
              classNames={{ root: classes.languageControl, label: classes.languageControlLabel }}
            />
          }
        >
          {t( 'preferences_language_label', 'Idioma' )}
        </Menu.Item>
      )}

      {/* Tutorials — nested submenu, same shape as the navbar overflow menu. */}
      <Menu position="right-start" offset={2} trigger="click-hover" closeDelay={150}>
        <Menu.Target>
          <Menu.Item
            py="sm"
            closeMenuOnClick={false}
            leftSection={<IconProgressHelp color={ICON_COLOR} size={14} />}
            rightSection={<IconChevronRight size={14} stroke={1.5} />}
          >
            {t( 'preferences_tutorials_label', 'Tutoriales' )}
          </Menu.Item>
        </Menu.Target>
        <Menu.Dropdown>
          {/* Shared with the mobile drawer's Tutoriales accordion — TV1-2294 ticket 28
              (GAP 3): the drawer rendered nothing at all in this case. */}
          {entries.length === 0 ? (
            <TourEntriesEmptyState />
          ) : (
            entries.map(( entry ) => (
              <Menu.Item
                key={entry.key}
                className={classes.tourItem}
                onClick={() => handleStartTour( entry.key )}
                rightSection={
                  <Badge size="xs" variant={entry.badge.variant} color={entry.badge.color}>
                    {entry.badge.label}
                  </Badge>
                }
              >
                <div className={classes.tourItemLabel}>{entry.name}</div>
                {entry.description && (
                  <Text c="dimmed" className={classes.tourItemDescription}>
                    {entry.description}
                  </Text>
                )}
              </Menu.Item>
            ))
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}
